'use client';

import { TELEMETRY_EVENTS, type TelemetryEventName } from '@/lib/telemetry/events';
import { getTelemetryContext } from '@/lib/telemetry/context';

type TelemetryValue = string | number | boolean | null | undefined;
type TelemetryParams = Record<string, TelemetryValue>;

type YmFn = (counterId: number, type: string, ...args: unknown[]) => void;

declare global {
  interface Window {
    ym?: YmFn;
  }
}

const COUNTER_ID = Number(process.env.NEXT_PUBLIC_YANDEX_METRIKA_ID ?? '');

function isTelemetryEnabled(): boolean {
  return Number.isFinite(COUNTER_ID) && COUNTER_ID > 0 && typeof window !== 'undefined' && typeof window.ym === 'function';
}

function sanitizeParams(params?: TelemetryParams): Record<string, string | number | boolean | null> {
  if (!params) return {};
  const safe: Record<string, string | number | boolean | null> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null) {
      safe[key] = value;
    } else {
      safe[key] = String(value);
    }
  }
  return safe;
}

export function trackEvent(eventName: TelemetryEventName, params?: TelemetryParams): void {
  if (!isTelemetryEnabled()) return;
  const withContext = {
    ...getTelemetryContext(),
    ...params,
  };
  window.ym?.(COUNTER_ID, 'reachGoal', eventName, sanitizeParams(withContext));
}

export function trackPageView(path: string, params?: TelemetryParams): void {
  if (!isTelemetryEnabled()) return;
  window.ym?.(COUNTER_ID, 'hit', path, {
    params: sanitizeParams({ ...getTelemetryContext(), ...params }),
  });
}

export function trackCtaClick(ctaName: string, params?: TelemetryParams): void {
  trackEvent(TELEMETRY_EVENTS.ctaClick, { cta_name: ctaName, ...params });
}

export function trackFunnelStep(step: string, action: 'view' | 'submit' | 'error', params?: TelemetryParams): void {
  const actionMap = {
    view: TELEMETRY_EVENTS.funnelStepView,
    submit: TELEMETRY_EVENTS.funnelStepSubmit,
    error: TELEMETRY_EVENTS.funnelStepError,
  } as const;
  trackEvent(actionMap[action], { step, ...params });
}

export function trackPaymentEvent(action: 'started' | 'success' | 'failed', params?: TelemetryParams): void {
  const actionMap = {
    started: TELEMETRY_EVENTS.paymentStarted,
    success: TELEMETRY_EVENTS.paymentSuccess,
    failed: TELEMETRY_EVENTS.paymentFailed,
  } as const;
  trackEvent(actionMap[action], params);
}

export function trackFrontendError(location: string, error: unknown, params?: TelemetryParams): void {
  const message = error instanceof Error ? error.message : String(error ?? 'unknown_error');
  trackEvent(TELEMETRY_EVENTS.frontendError, { location, message: message.slice(0, 500), ...params });
}
